/**
 * @fileoverview Input/output schemas for the pov-changer-v2 agent.
 *
 * This agent rewrites a chapter draft with a new point of view.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { POV_TYPES } from '@/domains/story-planning/constants';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 * Includes special fields for the target POV configuration.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.styleGuide.schema)
  .merge(AGENT_SCHEMA_OBJECTS.charactersCurrent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterContent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.draftingHistory.schema)
  .merge(z.object({
    currentPovType: z.string().optional().describe('The current POV type of the chapter (if known).'),
    currentNarrator: z.string().optional().describe('The current narrator character name (if applicable).'),
    newPovType: z.enum(POV_TYPES as [string, ...string[]]).describe('The target POV type to convert to.'),
    newNarrator: z.string().describe('The target narrator character name for the new POV.'),
  }));

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Unified output schema for content-producing agents.
 */
export const OutputSchema = z.object({
  commentary: z.string().describe(
    'Brief commentary explaining the POV change approach, what adjustments were made, and the scope of the rewrite (2-3 sentences). ' +
    'This will be recorded in the drafting history.'
  ),
  content: z.string().describe(
    'The fully rewritten chapter draft with the new POV, formatted as a single HTML string.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
